// Piece meshes, keyed by the square they occupy, with move tweening.
//
// A turn-based game changes state in discrete jumps and those jumps need
// narrating or players lose the thread (PLAN §5.7.6) — so a move is an arc
// tween rather than a teleport, and a capture sinks rather than vanishing.

import {
  type BufferGeometry,
  Group,
  type Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  type Texture,
  Vector3
} from "three";

import type { BoardFacts, Side } from "src/api";
import {
  type Color as SideColor,
  PAWN,
  type Piece,
  pieceColor,
  pieceType,
  type Direction,
  EAST,
  fileOf,
  forwardFacing,
  NORTH,
  rankOf,
  SOUTH,
  WEST,
  type PieceType
} from "src/shared/board";
import { VARIANT_PLAIN } from "src/shared/variants";
import { DIR_INDEX, packedPiece, VARIANT_ID } from "./factNames";
import { createChevronGeometry } from "./chevron";
import { descentAt } from "./descent";
import { Thrusters } from "./thrusters";
import { FACING_YAW, squareToWorld } from "./coords";
import { tintForArmy } from "./armyTint";
import { fadeBelowBoard } from "./riseFade";
import { Outlines } from "./outline";
import { PALETTE } from "./palette";
import type { PieceGeometries, VariantGeometries } from "./models";

/**
 * Where every body's front points in its own coordinates: north, which is
 * −Z, the direction `FACING_YAW` is built around.
 *
 * A contract rather than a measurement. `tools/exportPieces.py` turns the
 * base set to face this way on export, so the renderer needs no per-rank
 * correction; a pack authored facing elsewhere says so in `PIECE_YAW`, which
 * is where the classic knight's half turn lives. It used to be measured here
 * per set, and every replacement set moved it — `Math.PI / 2` for the first
 * carved set, `+Z` for the second — so every knight faced across its own move
 * until somebody looked.
 */
export const MODEL_FORWARD = { x: 0, z: -1 } as const;

/**
 * The set is authored to fit a 1.0 square exactly (king 0.805 wide), which
 * leaves almost no visual margin. Easing it down slightly buys margin on the tile
 * without costing the piece its presence — go much below 0.9 and the set starts
 * reading as discs rather than as chessmen at this pitch.
 */
const PIECE_SCALE = 0.95;

/**
 * An upgraded piece stands a little taller than its rank (the pip over it
 * says which row). Slight, so a 1.12 king still clears its square.
 */
const VARIANT_SWELL = 1.12;

/** What a view rests at: its rank's size, swollen when it wears a variant row. */
const restScale = (variant: number): number =>
  variant === VARIANT_PLAIN ? PIECE_SCALE : PIECE_SCALE * VARIANT_SWELL;

const MOVE_SECONDS = 0.26;
const ARC_HEIGHT = 0.55;
const FADE_SECONDS = 0.22;
/** A carry let go of nowhere slides back, quicker than a move. */
const RETURN_SECONDS = 0.16;
/** How long a dropped view waits for the move that re-keys it. */
const DROP_GRACE = 1.5;
/**
 * An action pulse: the piece swells and settles once.
 *
 * Short and small on purpose. It exists so that mining, handing material over
 * or minting is something you *see happen* to a specific piece — a turn used
 * to change several numbers at once with nothing on the board moving, so the
 * board never told you which piece had acted.
 */
const PULSE_SECONDS = 0.34;
const PULSE_SWELL = 0.16;

/**
 * A start: the piece jumps and lands.
 *
 * **Vertical, where `pulse` is a swell, and the difference is what each is
 * about.** A swell says *this piece acted* — mining, handing over, minting —
 * and it is a change of emphasis on something the piece chose to do. A bounce
 * says *something happened to it*: the cover is blown, and the piece is
 * reacting rather than working. Two readings, so two animations, and using the
 * swell for both would make a spy being unmasked look like a pawn digging.
 *
 * Two hops, damped, because one is a nudge and three is a cartoon. The decay
 * is what makes it read as landing rather than as hovering.
 */
const BOUNCE_SECONDS = 0.62;
const BOUNCE_HEIGHT = 0.34;
const BOUNCE_HOPS = 2;

/**
 * The arrival shot: how high a king starts, and how long it takes to get down.
 *
 * Twelve squares, which under this camera is comfortably off the top of the
 * viewport at the default zoom — the piece is meant to *enter* the frame, not
 * to be sitting at the top of it waiting to start. It falls unpowered for the
 * first half, so it is only off screen for about half a second.
 *
 * The whole thing is 1.5s, which is long enough to be a shot and short enough
 * that a player who has seen it forty times is not waiting on it. There is no
 * skip: the run's board is not interactive until it lands anyway, and a skip
 * control on a second and a half is a bigger interruption than the shot.
 */
const FLY_HEIGHT = 12;
const FLY_SECONDS = 1.5;

/**
 * How much of a piece is left while a belt tool is up.
 *
 * Faint enough that a lane under a king is plainly readable through it, and
 * not so faint that the army disappears — you are still laying track *around*
 * pieces, and where they are is half of what decides the route.
 */
const FADED_OPACITY = 0.3;

/**
 * Which way a knight should face after moving from `from` to `to`.
 *
 * The dominant leg of the L. A knight's move is two squares one way and one
 * the other, and the two-square leg is the one the move reads as — a knight
 * that went two east and one north went *east*.
 *
 * In the set's own frame (`MODEL_FORWARD`); a pack's correction is added by
 * the caller.
 */
/**
 * An angle folded into (-pi, pi], so a turn is drawn the short way round.
 *
 * A pawn pulled from north to west is a quarter turn one way and three
 * quarters the other, and the raw difference between two `FACING_YAW` entries
 * is as likely to be the second as the first.
 */
export const shortWay = (angle: number): number => {
  const turn = Math.PI * 2;
  return ((((angle + Math.PI) % turn) + turn) % turn) - Math.PI;
};

export function knightYaw(from: number, to: number): number {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  const facing: Direction =
    Math.abs(df) >= Math.abs(dr) ? (df >= 0 ? EAST : WEST) : dr >= 0 ? NORTH : SOUTH;
  return FACING_YAW[facing];
}

interface PieceView {
  group: Group;
  /** The piece's own mesh, apart from the chevron a pawn also carries. */
  mesh: Mesh;
  piece: Piece;
  /** The `PIECE_VARIANTS` id it was built as, so a purchase rebuilds the view. */
  variant: number;
  /**
   * Set while the view is tweening between squares.
   *
   * `seconds` and `arc` are on the motion rather than on the view because a
   * drop sliding home is not a move.
   */
  motion?: {
    from: Vector3;
    to: Vector3;
    elapsed: number;
    seconds: number;
    arc: number;
    /**
     * Seconds to wait before starting — what a **staggered** body is made of.
     *
     * A property of the motion rather than of a queue somewhere, because the
     * views are already independent of each other.
     */
    delay?: number;
    /**
     * Yaw to turn through while travelling, for a piece whose facing changed
     * with the step. Interpolated the short way round.
     */
    spin?: { from: number; to: number };
    /** A carry put back where it came from: nothing landed, so nothing says so. */
    quiet?: boolean;
  };
  /** Set while a captured view is sinking out of the scene. */
  fading?: number;
  /**
   * In the player's hand (`hold`): nothing here writes its position.
   *
   * `grace` is set once it has been dropped on a square, and counts down to
   * going home — a move the reducer took re-keys the view long before, and one
   * it refused, or a room that never answers, must not leave it standing there.
   */
  held?: { home: number; grace?: number };
  /** Knights only: the way it is facing, from the last move it made. */
  knightYaw?: number;
  /** Seconds into an action pulse, if one is running. */
  pulse?: number;
  /** Seconds into a reaction bounce, if one is running. */
  bounce?: number;
  /**
   * How much of `position.y` is this frame's hop.
   *
   * **Carried, so the pass can undo itself.** The bounce runs last and *adds*
   * to whatever the frame's other passes decided — a move's arc, a square
   * still rising out of the board — because a piece can be
   * unmasked by the very move it is making, and a bounce that set `y`
   * absolutely would cancel the hop it is riding on. Adding needs a way back:
   * every other pass writes `y` wholesale, but a view at rest has nobody
   * writing it at all, and there the addition would accumulate.
   */
  bounceY?: number;
  /** The inverted hull round it, and the colour it is wearing. */
  outline?: { mesh: Mesh; color: number };
  /** Flying itself in: seconds elapsed, and the flame it is doing it with. */
  descent?: { elapsed: number; square: number; flame: Thrusters };
}

/** One army's worth of pack: what it is made of, and which way it faces. */
interface PackParts {
  geometries: PieceGeometries;
  /** The variants' own bodies, where the pack has them. */
  variants?: VariantGeometries;
  materials?: Partial<Record<PieceType, Material>>;
  /** Where the army's colour goes, when the pack's art says. See `LoadedPack`. */
  masks?: Partial<Record<PieceType, Texture>>;
  /** How hard that colour is pushed. See `PACK_PAINT`. */
  paint: number;
  packYaw: number;
  pieceYaw: Partial<Record<PieceType, number>>;
}

export class PieceViews {
  readonly group = new Object3D();
  private views = new Map<number, PieceView>();
  /**
   * The pack each army is wearing, and the one everybody else wears.
   *
   * **Per army rather than per board.** A pack used to be a global swap, which
   * is the right shape while it is a dev toggle for looking at art and the
   * wrong one the moment a *camp* is supposed to look like a camp: the whole
   * point of a second set of models is that the thing walking toward you is
   * visibly not made of the same stuff you are. Keyed by side, with `default`
   * for every army nobody has dressed.
   */
  private packs = new Map<number | "default", PackParts>();
  /** Per (type, side) clones of a pack's materials, tinted. `type * 8 + side`. */
  private packSide = new Map<number, Material>();
  /**
   * Views in flight, keyed by the square each one **left**.
   *
   * The odd key is the point. A move re-keys `views` to the destination
   * immediately, but the factory is a beat behind — `BoardRenderer` holds the
   * new industry back until the piece lands — so for the whole of a move the
   * ingots in a piece's hands are still drawn on the square it started from.
   * That is the square anything wanting to travel with the piece has to ask
   * about, and `views` cannot answer it.
   *
   * `from` is the anchor the offset is measured against, held separately
   * because a second move re-keys the view and replaces its `motion`: the
   * entry for the original square must keep measuring from the original
   * square, or a piece that moves twice before the factory catches up drags
   * its ingots by the wrong displacement.
   */
  private travelling = new Map<number, { view: PieceView; from: Vector3 }>();

  private fading: PieceView[] = [];
  private materials = new Map<SideColor, MeshStandardMaterial>();
  private chevronMaterial: MeshStandardMaterial;
  private chevronGeometry: BufferGeometry;
  /** True while the army is drawn back for a gesture about the floor. */
  private faded = false;

  /**
   * Draw the army back, or bring it forward again.
   *
   * Three things at once, because two of them alone read as a bug rather than
   * as a fade: the opacity, `depthWrite` (a translucent piece that still
   * writes depth hides the very lane you are looking through it at), and the
   * shadow (a solid black shadow under a ghost is the piece asserting itself
   * again from the floor, right where the belt is being drawn).
   *
   * Applied to the materials rather than per view, since they are shared per
   * side and per pack — and the two places that mint one late, `materialFor`
   * and `pieceMaterial`, go through the same call so a piece that arrives
   * mid-gesture arrives faded like the rest.
   */
  setFaded(faded: boolean): void {
    if (this.faded === faded) return;
    this.faded = faded;
    this.refade();
    // A rim at full strength round a ghosted piece reads as the fade having
    // failed on that one piece.
    this.outlines.setFaded(faded);
    for (const view of this.views.values()) view.mesh.castShadow = !faded;
  }

  /**
   * Is the board still arriving under these pieces?
   *
   * A camp lands its army on ground that is still coming up, so a piece has to
   * be as invisible as the tile it is standing on until both clear the floor
   * (`riseFade.ts`). The pieces cannot work this out — the offset is the
   * tiles' — so `BoardRenderer` passes it down.
   *
   * **Two reasons, one flag.** `transparent` is also what the belt tool's
   * ghosting drives, so a rise settling while a lane is being drawn would
   * otherwise switch the army back to solid. `refade` takes the *or* of them.
   */
  setRising(rising: boolean): void {
    if (this.rising === rising) return;
    this.rising = rising;
    this.refade();
  }

  private rising = false;

  private refade(): void {
    for (const material of this.materials.values()) this.applyFade(material);
    for (const material of this.packSide.values()) this.applyFade(material);
    this.applyFade(this.chevronMaterial);
  }

  private applyFade<T extends Material>(material: T): T {
    // The ghost's own alpha is a *value*; the rise's is a shader multiplying
    // whatever that value is. So only the ghost sets `opacity`, and the flag
    // that decides whether either is honoured is the or of the two.
    material.transparent = this.faded || this.rising;
    material.opacity = this.faded ? FADED_OPACITY : 1;
    // Kept while merely rising: a piece coming up through the floor is at
    // alpha 1 above the plane, so dropping depth writes would let the board
    // sort through it. The ghost still drops them, for the reason above.
    material.depthWrite = !this.faded;
    material.needsUpdate = true;
    return material;
  }

  /**
   * Swap the piece models without rebuilding anything else.
   *
   * The instanced pools are keyed by piece *type*, and a pack changes only
   * what each type looks like — so the pools are dropped and rebuilt on the
   * next sync rather than the whole scene being torn down. Dev-only today
   * (`PIECE_PACKS`), and cheap enough to be live.
   */
  setGeometries(
    geometries: PieceGeometries,
    packMaterials?: Partial<Record<PieceType, Material>>,
    /** A whole-pack rotation correction. See `PACK_YAW`. */
    packYaw?: number,
    /** Per-rank corrections. See `PIECE_YAW`. */
    pieceYaw?: Partial<Record<PieceType, number>>,
    /** Which army wears it, or every undressed one. */
    side: number | "default" = "default",
    /** Where the army's colour goes. See `LoadedPack.masks`. */
    packMasks?: Partial<Record<PieceType, Texture>>,
    /** How hard it is pushed. See `PACK_PAINT`. */
    packPaint = 1,
    /** The variants' bodies. See `LoadedPack.variants`. */
    packVariants?: VariantGeometries
  ): void {
    if (side === "default") this.geometries = geometries;
    this.packs.set(side, {
      geometries,
      variants: packVariants,
      materials: packMaterials,
      masks: packMasks,
      paint: packPaint,
      packYaw: packYaw ?? 0,
      pieceYaw: pieceYaw ?? {}
    });
    // Drop the per-side clones so the next mesh rebuilds them from whatever
    // this pack supplies — palette colours for the base set, the pack's own
    // baked material otherwise.
    for (const material of this.packSide.values()) material.dispose();
    this.packSide.clear();
    // Removed, **never disposed**. These geometries are loaded once and shared
    // by every mesh of that rank, and `loadPiecePack` caches them per pack — so
    // disposing one here would destroy the base set for anyone who switched
    // back to it, and the failure would be an empty board rather than an
    // error. The next `sync` rebuilds the meshes against the new set.
    //
    // **Anything mid-flight ends here**, and the caller has to know. A view is
    // where the descent lives, so clearing them silently cancelled a king that
    // was on its way in — and `setPiecePack("base")` runs on *every* mount,
    // resolving a few hundred milliseconds after a run opens. That is exactly
    // when the shot is playing, so it never once finished. `BoardRenderer`
    // re-arms and relaunches on the fresh views.
    for (const view of this.views.values()) {
      view.descent?.flame.dispose();
      view.group.removeFromParent();
    }
    this.views.clear();
  }

  constructor(
    private geometries: PieceGeometries,
    /**
     * The shell workshop, shared with the buildings so one resize reaches
     * every rim. Its own where a caller has none, which is what lets a test
     * build views out of nothing but geometry.
     */
    private outlines = new Outlines(),
    /** The base set's variant bodies. None, for a test built on geometry alone. */
    private variants: VariantGeometries = new Map()
  ) {
    // One geometry set for every army — the two chess .blend files are
    // geometrically identical and differ only in material (PLAN §5.7.1), and
    // that is what lets a third and fourth side cost a colour rather than a
    // model. Made on demand, since most games only ever see two.
    this.materials.set(0, this.makeMaterial(PALETTE.lightPiece));
    this.materials.set(1, this.makeMaterial(PALETTE.darkPiece));
    // **Scenery, not an overlay.** The chevron drew with `depthTest` off from
    // the first render until the walker set: a floor mark pointing away from
    // the camera cannot clear the piece on its own square (chevron.ts), and
    // the carved pawn had no other way to show where it faced. The walker pawn
    // has a front, so the chevron is a reinforcement of what the body already
    // says and it draws where it lies — a chevron behind its pawn is hidden
    // by that pawn, on purpose, rather than drawn through it.
    // The pawn's facing mark rises with the pawn, so it takes the fade too.
    this.chevronMaterial = fadeBelowBoard(
      new MeshStandardMaterial({
        color: PALETTE.accent,
        roughness: 0.5,
        metalness: 0
      })
    );
    this.chevronGeometry = createChevronGeometry();
  }

  /**
   * The material for one rank of one side.
   *
   * A pack's own material when it has one, cloned per side and **tinted**
   * rather than replaced: the baked map is most of what a generated set looks
   * like, and painting over it in the palette's two colours would throw the
   * art away — which is exactly what the first version of this did. Side 0
   * keeps the texture as authored; side 1 is darkened, so the two armies read
   * apart the way they always have without either losing its detail.
   */
  private pieceMaterial(type: PieceType, side: number): Material {
    const pack = this.packFor(side);
    const source = pack.materials?.[type];
    if (!source) return this.materialFor(side);
    const key = type * 8 + side;
    const existing = this.packSide.get(key);
    if (existing) return existing;
    // Painted where the pack's art says where its paint goes, and hue-rotated
    // where it does not — see `armyTint.ts`. Either way the map is sampled as
    // authored, so the shading, wear and edge detail all survive; what a mask
    // adds is that only the *panels* move, and the copper stays copper.
    // `fadeBelowBoard` last, so it composes *over* the tint's own
    // `onBeforeCompile` rather than being overwritten by it — the paint mask
    // and the rise fade are both hooks on one material, and the order they are
    // added in is the order they wrap.
    const clone = this.applyFade(
      fadeBelowBoard(tintForArmy(source.clone(), side, pack.masks?.[type], pack.paint))
    );
    this.packSide.set(key, clone);
    return clone;
  }

  /**
   * The pack this army is wearing.
   *
   * Falls back to the undressed default and then to the base set, so a side
   * nobody has dressed — a third army, a wave under a new flag — is a piece
   * that draws rather than a hole in the board.
   */
  private packFor(side: number): PackParts {
    return (
      this.packs.get(side) ??
      this.packs.get("default") ?? {
        geometries: this.geometries,
        variants: this.variants,
        paint: 1,
        packYaw: 0,
        pieceYaw: {}
      }
    );
  }

  /** A pack's correction for one rank of one army, in radians. */
  private packRotation(type: PieceType, side: number): number {
    const pack = this.packFor(side);
    return pack.packYaw + (pack.pieceYaw[type] ?? 0);
  }

  private makeMaterial(color: number): MeshStandardMaterial {
    // A piece standing on ground that has not arrived is as invisible as the
    // ground is, and comes up through the floor with it. See `riseFade.ts`.
    return fadeBelowBoard(new MeshStandardMaterial({ color, roughness: 0.55, metalness: 0 }));
  }

  /** The material for an army, minting one the first time a side appears. */
  private materialFor(side: SideColor): MeshStandardMaterial {
    const existing = this.materials.get(side);
    if (existing) return existing;
    const palette = PALETTE.extraPieces;
    const material = this.applyFade(this.makeMaterial(palette[(side - 2) % palette.length]));
    this.materials.set(side, material);
    return material;
  }

  private createView(
    piece: Piece,
    sq: number,
    facing: number,
    army: Direction,
    variant: number
  ): PieceView {
    const group = new Group();
    // A variant's own body where the pack has one, else the rank's: the DAM
    // has a drone and the turbo pair has nothing but a longer reach.
    const pack = this.packFor(pieceColor(piece));
    const mesh = new Mesh(
      pack.variants?.get(variant) ?? pack.geometries[pieceType(piece)],
      this.pieceMaterial(pieceType(piece), pieceColor(piece))
    );
    mesh.castShadow = !this.faded;
    mesh.receiveShadow = true;
    group.add(mesh);

    if (pieceType(piece) === PAWN) {
      const chevron = new Mesh(this.chevronGeometry, this.chevronMaterial);
      // Too thin to throw a shadow worth the draw, and it lies in its pawn's.
      chevron.castShadow = false;
      chevron.receiveShadow = true;
      group.add(chevron);
      group.rotation.y = FACING_YAW[facing];
    }

    // A knight or a bishop arrives looking the way its army is looking.
    //
    // Both can show a direction — the knight is not a body of revolution and
    // the bishop's mitre is cut — so a forged one facing an arbitrary north
    // stands out as *wrong* among pieces that came with the army. Purely
    // visual: neither has a facing in the rules.
    const type = pieceType(piece);
    // **Every piece faces somewhere now, not only the two the old set could
    // show it on.**
    //
    // This used to be knights and bishops alone, and the reasoning held while
    // the purchased set was the only set: five of its six ranks are bodies of
    // revolution, so rotating them is a visual no-op and there was nothing to
    // be right or wrong about. An AI-generated pack is asymmetric on every
    // rank — each model has a front — so a rook that never turns simply faces
    // whichever way it was authored, for the whole game, which is what "the
    // packs are oriented poorly" actually is.
    //
    // Still purely visual. Facing is a *pawn's* rule and giving it to the
    // other ranks would put it in the repetition key and in check detection
    // for no gain (a rook attacks the same squares whichever way it looks).
    group.rotation.y = FACING_YAW[army] + this.packRotation(type, pieceColor(piece));

    group.scale.setScalar(restScale(variant));
    squareToWorld(sq, this.groundY(sq), group.position);
    this.group.add(group);
    return {
      group,
      mesh,
      piece,
      variant,
      // Where it is looking. Named for the knight because it was the knight's
      // alone; every rank keeps one now.
      knightYaw: group.rotation.y
    };
  }

  /**
   * Ground height per square, so a piece standing on one of its own belt parts
   * rests on the squashed deck instead of inside it. Everything that positions
   * a view goes through this rather than assuming y=0.
   */
  private lift: (square: number) => number = () => 0;
  /**
   * How far the *square itself* is from being seated, which is not zero while
   * new ground is rising into place. Separate from `lift` because they change
   * on different clocks: a belt deck moves when the state does, a rise moves
   * every frame for 0.45s. Added together everywhere a view is placed.
   */
  private ground: (square: number) => number = () => 0;

  setGround(ground: (square: number) => number): void {
    this.ground = ground;
  }

  /** Where a view standing on this square belongs. */
  private groundY(square: number): number {
    return this.lift(square) + this.ground(square);
  }

  setLift(lift: (square: number) => number): void {
    this.lift = lift;
    this.refreshGround();
  }

  /**
   * Re-seat every settled view. Called per frame while the board is rising, so
   * a piece a wave landed on new ground comes up welded to it rather than
   * hanging over the hole until the floor arrives.
   */
  refreshGround(): void {
    for (const [sq, view] of this.views) {
      // A descending one is deliberately twelve squares above the height this
      // would put it at.
      if (view.motion || view.descent || view.held) continue;
      view.group.position.y = this.groundY(sq);
    }
  }

  /**
   * Reconcile against the board's facts. `moved` (from -> to) lets a move
   * animate instead of destroy-and-recreate; pass the castling rook as a
   * second entry.
   *
   * **Facts and nothing else** (PLAN-CLIENT-SPLIT days 3–4): a piece is
   * `facts.squares[sq].piece`, translated to the packed keys the views are
   * built on by `factNames.ts`. Nothing here asks the rules.
   */
  sync(
    facts: BoardFacts,
    moved: readonly { from: number; to: number }[] = [],
    /**
     * Seconds between one mover setting off and the next.
     *
     * Nought for every ordinary turn — a move is one piece, and a castle's
     * rook goes with its king rather than after it. Nothing passes one since
     * the Pattern King retired (§31 R2).
     */
    stagger = 0
  ): void {
    // Ascending square order, as the facts come. `facing` and `variant` are
    // read here once so the passes below never go back to the facts.
    const next = new Map<number, { piece: Piece; facing: Direction; variant: number }>();
    for (const [sq, square] of facts.squares) {
      const piece = square.piece;
      if (!piece) continue;
      next.set(sq, {
        piece: packedPiece(piece),
        facing: DIR_INDEX[piece.facing],
        variant: VARIANT_ID[piece.variant]
      });
    }
    // The way an army looks, else the way it marches — a side the facts have
    // not voted on (nothing living, or a ghost's army) still faces somewhere.
    const armyOf = (side: number): Direction => {
      const named = facts.armyFacing.get(side as Side);
      return named ? DIR_INDEX[named] : forwardFacing(side);
    };

    // Re-key the views that moved before diffing, so a move reads as one view
    // travelling rather than one disappearing and another appearing.
    for (const [index, { from, to }] of moved.entries()) {
      const view = this.views.get(from);
      if (!view) continue;
      this.views.delete(from);

      const displaced = this.views.get(to);
      if (displaced) this.beginFade(displaced);

      this.views.set(to, view);

      // A knight turns to face the way it went. Its move is two squares one
      // way and one the other, so "the way it went" is the *dominant* axis —
      // the long leg of the L, which is what the move reads as.
      //
      // It is the one piece in the set that is not a body of revolution, so it
      // is the one piece a rotation says anything about. Purely visual: facing
      // is a pawn's rule, and giving the knight one would put it in the
      // repetition key and in check detection for no gain.
      // **Turn to face the way it moved**, whichever rank it is. A piece that
      // walked north and is still looking east reads as a bug on a set where
      // every model has a front — and "the direction it just travelled" is the
      // only direction a piece is known to care about that the rules do not
      // already own.
      view.knightYaw =
        knightYaw(from, to) + this.packRotation(pieceType(view.piece), pieceColor(view.piece));

      // **Dropped here by hand**, so it is already over the square and has
      // been lifted and carried once: a second hop is the move played twice.
      const carried = view.held !== undefined;
      view.held = undefined;

      // What the facts say is standing where it went — its facing, for a
      // pawn pulled round a corner.
      const arrived = next.get(to);
      view.motion = {
        from: view.group.position.clone(),
        to: squareToWorld(to, this.groundY(to)),
        elapsed: 0,
        seconds: MOVE_SECONDS,
        // A body slides; it does not hop. The arc is what makes a chess move
        // read as lifting and placing a piece, and five pieces hopping in
        // sequence reads as five pieces rather than as one thing.
        arc: stagger > 0 || carried ? 0 : ARC_HEIGHT,
        delay: stagger * index,
        // Only where the facing actually moved. `aimOf` turns a pulled pawn to
        // face the way it was dragged, and this is that turn, drawn.
        spin:
          stagger > 0 && pieceType(view.piece) === PAWN && arrived
            ? { from: view.group.rotation.y, to: FACING_YAW[arrived.facing] }
            : undefined
      };
      // Whatever it is carrying is still drawn back there. `motion.from` is a
      // fresh vector each move and is only ever read, so the entry can hold
      // the reference and go on measuring from this square after a later move
      // has given the view a different `motion`.
      this.travelling.set(from, { view, from: view.motion.from });
    }

    for (const [sq, view] of [...this.views]) {
      const wanted = next.get(sq);
      // A promotion changes the piece on a square it just moved to, so compare
      // identity, not just presence — and a purchase changes the body on a
      // square nothing moved on, so compare the variant too.
      if (wanted && wanted.piece === view.piece && wanted.variant === view.variant) continue;
      this.views.delete(sq);
      this.beginFade(view);
    }

    for (const [sq, { piece, facing, variant }] of next) {
      if (!this.views.has(sq)) {
        this.views.set(
          sq,
          this.createView(piece, sq, facing, armyOf(pieceColor(piece)), variant)
        );
      }
    }

    // A pawn that was re-aimed did not move, so the diff above leaves its view
    // alone — rotate it in place. Because the pawn is a body of revolution,
    // this reads as nothing at all except the chevron swinging round, which is
    // exactly what makes a knight's re-aim legible.
    for (const [sq, view] of this.views) {
      // A piece part way through a turn of its own is not snapped to the end
      // of it: that is the difference between a body turning a corner and a
      // body that arrives already facing the new way.
      if (view.motion?.spin) continue;
      if (pieceType(view.piece) === PAWN) {
        const standing = next.get(sq);
        if (!standing) continue;
        const wanted = FACING_YAW[standing.facing];
        if (view.group.rotation.y !== wanted) view.group.rotation.y = wanted;
        continue;
      }
      if (view.knightYaw !== undefined && view.group.rotation.y !== view.knightYaw) {
        view.group.rotation.y = view.knightYaw;
      }
    }

    // Last, so a view built or re-keyed by this pass wears whatever the
    // highlights already say about the square it is on.
    this.applyOutlines();
  }

  /**
   * Which pieces wear a rim, and in what colour. Squares with no piece on them
   * are ignored — the same map goes to the buildings, and a square is one or
   * the other.
   *
   * Remembered rather than applied and forgotten, because a view can arrive
   * *after* the highlight does: the models land a few hundred milliseconds
   * into a game, and a piece minted or carried onto a highlighted square is
   * built by the next `sync`. Re-applied at the end of every one.
   */
  private outlined: ReadonlyMap<number, number> = new Map();

  setOutlines(outlined: ReadonlyMap<number, number>): void {
    this.outlined = outlined;
    this.applyOutlines();
  }

  private applyOutlines(): void {
    for (const [square, view] of this.views) {
      this.dressOutline(view, this.outlined.get(square));
    }
  }

  /** Put the shell on, take it off, or repaint it. */
  private dressOutline(view: PieceView, color: number | undefined): void {
    const current = view.outline;
    if (color === undefined) {
      if (current) {
        current.mesh.removeFromParent();
        view.outline = undefined;
      }
      return;
    }
    if (current) {
      if (current.color === color) return;
      current.mesh.material = this.outlines.materialFor(color);
      current.color = color;
      return;
    }

    const mesh = new Mesh(
      this.outlines.hullFor(view.mesh.geometry),
      this.outlines.materialFor(color)
    );
    // A mark, not a solid: it lights nothing and blocks nothing. And it is not
    // a thing on the board, so it is not a pick target either — the piece
    // underneath already is, and two hits on one square is how a click starts
    // meaning something else.
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.raycast = () => {};
    view.group.add(mesh);
    view.outline = { mesh, color };
  }

  private beginFade(view: PieceView): void {
    view.fading = 0;
    view.motion = undefined;
    // Whatever it was highlighted for is over: it has been taken, or promoted
    // into a piece that is a different thing on the same square.
    this.dressOutline(view, undefined);
    // Whatever it was doing is over. A flame left on a view that is sinking
    // out of the scene goes down through the board still burning.
    view.descent?.flame.dispose();
    view.descent = undefined;
    // Its motion is over, however it ended. A view that sinks while still
    // listed as travelling would drag a pile of ingots down through the board
    // with it — and a captured piece's material has already been spilled by
    // the rules, so there is nothing left for it to carry.
    this.landed(view);
    this.fading.push(view);
  }

  /**
   * Fly the piece on this square in from above, under its own power.
   *
   * The opening shot of a run. Only the king is ever asked, and only once —
   * `BoardRenderer` owns that decision, because "is this the start of a run"
   * is a question about the game and not about the scene.
   *
   * Returns false when there is nobody there to fly, which is the honest
   * answer while the models are still loading: the caller tries again on the
   * sync that happens when they land.
   */
  flyIn(square: number): boolean {
    const view = this.views.get(square);
    if (!view || view.descent) return false;

    const flame = new Thrusters();
    view.group.add(flame.group);
    view.descent = { elapsed: 0, square, flame };
    // Put it at the top of its flight *now*, rather than waiting for the first
    // `update`: the board is drawn as soon as it is synced, and a king that
    // spends one frame standing on its square before leaping into the sky has
    // given the whole shot away.
    view.group.position.y = this.groundY(square) + FLY_HEIGHT;
    return true;
  }

  /** Is anything still flying itself in? The shot holds the board's opening. */
  get landing(): boolean {
    for (const view of this.views.values()) if (view.descent) return true;
    return false;
  }

  /** Start an action pulse on a square's piece, if there is one there. */
  pulse(square: number): void {
    const view = this.views.get(square);
    if (view) view.pulse = 0;
  }

  /**
   * Make a square's piece jump. True when there was one to jump.
   *
   * The return value is what a caller uses to decide whether the moment is
   * worth borrowing the camera for: a reaction nobody can see is not one, and
   * `flyIn` reports the same way for the same reason.
   */
  bounce(square: number): boolean {
    const view = this.views.get(square);
    if (!view) return false;
    // Restarted rather than stacked. Two reactions on one piece in one turn is
    // possible — a costume that came off the same turn something else happened
    // to it — and adding the heights would throw the piece off the board.
    view.bounce = 0;
    return true;
  }

  /**
   * Where the piece that left `square` has got to, as a displacement from it,
   * or null if nothing is in flight from there.
   *
   * The whole arc, plane and hop together, so anything riding along gets the
   * same curve rather than a second easing that has to be kept in step.
   */
  motionOffset(square: number, out: Vector3): Vector3 | null {
    const trip = this.travelling.get(square);
    if (trip) return out.subVectors(trip.view.group.position, trip.from);
    return null;
  }

  /**
   * A piece finished travelling, and the square it stopped on.
   *
   * **The landing, never the fade.** `beginFade` calls `landed` too — a taken
   * piece's trip ends when it starts sinking — and that is deliberately not
   * this: a capture is announced by the *capturer* arriving, so hanging the
   * hook off `landed` would say it twice, a fifth of a second apart.
   */
  private onLanded?: (square: number) => void;

  setOnLanded(onLanded: (square: number) => void): void {
    this.onLanded = onLanded;
  }

  /**
   * Forget every square this view was travelling from.
   *
   * A view can hold more than one entry — two moves before the factory caught
   * up — and they all end together, because they all end when the model stops.
   */
  private landed(view: PieceView): void {
    for (const [sq, trip] of this.travelling) {
      if (trip.view === view) this.travelling.delete(sq);
    }
  }

  /** Advance animations. Returns true while anything is still moving. */
  update(dt: number): boolean {
    let busy = false;

    for (const view of this.views.values()) {
      if (view.pulse === undefined) continue;
      view.pulse += dt;
      const t = Math.min(1, view.pulse / PULSE_SECONDS);
      // One half-sine: out and back, no overshoot to unwind.
      const swell = Math.sin(Math.PI * t) * PULSE_SWELL;
      view.group.scale.setScalar(restScale(view.variant) * (1 + swell));

      if (t >= 1) {
        view.group.scale.setScalar(restScale(view.variant));
        view.pulse = undefined;
      } else {
        busy = true;
      }
    }

    for (const view of this.views.values()) {
      const descent = view.descent;
      if (!descent) continue;

      descent.elapsed += dt;
      const flight = descentAt(
        Math.min(1, descent.elapsed / FLY_SECONDS),
        descent.elapsed,
        FLY_SECONDS
      );
      view.group.position.y = this.groundY(descent.square) + flight.height * FLY_HEIGHT;
      descent.flame.set(flight.throttle, descent.elapsed);

      if (flight.done) {
        descent.flame.dispose();
        view.descent = undefined;
        view.group.position.y = this.groundY(descent.square);
      }
      busy = true;
    }

    for (const view of this.views.values()) {
      if (view.held?.grace === undefined) continue;
      view.held.grace -= dt;
      if (view.held.grace <= 0) this.sendHome(view);
      busy = true;
    }

    // **Over the entries, for the square.** A view is keyed by where it is
    // going, which is the one thing a landing has to say and the one thing
    // `view` alone cannot answer.
    for (const [sq, view] of this.views) {
      const motion = view.motion;
      if (!motion) continue;

      motion.elapsed += dt;
      // A staggered link has not set off yet. It is still *busy* — the loop
      // has to keep drawing, or the frame after the head lands is the last one
      // and the tail never moves.
      const delay = motion.delay ?? 0;
      const t = Math.min(1, Math.max(0, (motion.elapsed - delay) / motion.seconds));
      // Smoothstep in the plane, a sine hop in Y: reads as lifting and placing.
      // A belt's ride sets `arc` to zero — it is a shunt, not a step.
      const eased = t * t * (3 - 2 * t);
      view.group.position.lerpVectors(motion.from, motion.to, eased);
      view.group.position.y += Math.sin(Math.PI * t) * motion.arc;
      // **Turning while it travels**, which is what a body does: the pawn is
      // pulled round the corner rather than snapping to its new facing on the
      // frame it arrives. The short way round — a quarter turn drawn as three
      // quarters the other way is a piece spinning on the spot.
      if (motion.spin) {
        const delta = shortWay(motion.spin.to - motion.spin.from);
        view.group.rotation.y = motion.spin.from + delta * eased;
      }

      if (t >= 1) {
        view.group.position.copy(motion.to);
        view.motion = undefined;
        this.landed(view);
        if (!motion.quiet) this.onLanded?.(sq);
      } else {
        busy = true;
      }
    }

    for (let i = this.fading.length - 1; i >= 0; i--) {
      const view = this.fading[i];
      view.fading = (view.fading ?? 0) + dt;
      const t = Math.min(1, view.fading / FADE_SECONDS);
      view.group.position.y = -t * 1.2;
      view.group.scale.setScalar(restScale(view.variant) * (1 - t * 0.35));

      if (t >= 1) {
        this.group.remove(view.group);
        this.fading.splice(i, 1);
      } else {
        busy = true;
      }
    }

    // **The bounce goes last, and it adds.** Every pass above writes `y`
    // wholesale — a move's arc, a ride, a rising square — and a piece is
    // routinely unmasked by the very move it is making, so a bounce that set
    // `y` would flatten the hop it is supposed to sit on top of. Undoing last
    // frame's contribution first is what keeps that exact: a view at rest has
    // nobody rewriting `y`, and there a bare addition would climb forever.
    for (const view of this.views.values()) {
      if (view.bounce === undefined && view.bounceY === undefined) continue;
      view.group.position.y -= view.bounceY ?? 0;
      view.bounceY = undefined;
      if (view.bounce === undefined) continue;

      view.bounce += dt;
      const t = Math.min(1, view.bounce / BOUNCE_SECONDS);
      if (t >= 1) {
        view.bounce = undefined;
        continue;
      }
      // `abs(sin)` is the hop: it touches zero between bounces, which is the
      // landing — a plain sine would sink the piece through the deck on the
      // second half of every cycle. Damped linearly, so the last hop is small
      // and the thing stops rather than fading out.
      const hop = Math.abs(Math.sin(Math.PI * t * BOUNCE_HOPS)) * BOUNCE_HEIGHT * (1 - t);
      view.bounceY = hop;
      view.group.position.y += hop;
      busy = true;
    }

    return busy;
  }

  /** Where a body standing on this square rests. */
  seatY(square: number): number {
    return this.groundY(square);
  }

  /**
   * Hand the view on this square to a carry (`BoardRenderer.beginCarry`).
   *
   * **By reference, never by square.** A drop re-keys the view in `sync`, and
   * which of that and `release` React commits first is not ours to say — a
   * release that looked the view up again would miss it, or find the piece
   * that was standing where it went.
   */
  hold(square: number): { object: Object3D; release(dropped: boolean): void } | null {
    const view = this.views.get(square);
    if (!view || view.fading !== undefined || view.descent) return null;
    view.motion = undefined;
    view.held = { home: square };
    return {
      object: view.group,
      release: (dropped) => {
        // `sync` already took it: the move was played and is being drawn.
        if (!view.held) return;
        if (dropped) view.held.grace = DROP_GRACE;
        else this.sendHome(view);
      }
    };
  }

  /** Put a carried view back on the square it was taken from. */
  private sendHome(view: PieceView): void {
    const home = view.held?.home;
    view.held = undefined;
    if (home === undefined || this.views.get(home) !== view) return;
    view.motion = {
      from: view.group.position.clone(),
      to: squareToWorld(home, this.groundY(home)),
      elapsed: 0,
      seconds: RETURN_SECONDS,
      arc: 0,
      quiet: true
    };
  }

  /**
   * A body for a piece that is not on the board yet — a forge being dragged
   * off the hotbar. Built exactly as a view is and never entered in `views`,
   * so nothing here animates, picks or diffs it.
   */
  createGhost(piece: Piece, facing: Direction, army: Direction): Object3D {
    return this.createView(piece, 0, facing, army, VARIANT_PLAIN).group;
  }

  discardGhost(ghost: Object3D): void {
    this.group.remove(ghost);
  }

  /**
   * Y rotation of the pawn view on a square, or null. Facing is invisible on a
   * body of revolution *except* through the chevron, so a test that only reads
   * rules state would not notice the indicator failing to turn.
   */
  facingYaw(square: number): number | null {
    const view = this.views.get(square);
    return view && pieceType(view.piece) === PAWN ? view.group.rotation.y : null;
  }

  /** Meshes to raycast for picking, with the square each belongs to. */
  pickTargets(): { object: Object3D; square: number }[] {
    // A held view is under the pointer wherever the pointer goes.
    return [...this.views]
      .filter(([, view]) => !view.held)
      .map(([square, view]) => ({ object: view.group, square }));
  }

  dispose(): void {
    for (const view of this.views.values()) view.descent?.flame.dispose();
    for (const material of Object.values(this.materials)) material.dispose();
    this.chevronMaterial.dispose();
    this.chevronGeometry.dispose();
    this.views.clear();
    this.travelling.clear();
    this.fading = [];
  }
}
